import React, { useEffect, useRef, useState } from 'react';
import { FaTelegram } from 'react-icons/fa';
import { LuCircleCheck, LuExternalLink, LuLoader } from 'react-icons/lu';

import { getTelegramLink } from '../../shared/api/notifications';

interface Props {
  isConnected: boolean;
  externalId?: string;
  linkedAt?: string;
  onRefresh: () => void;
  onDisconnect: () => Promise<void>;
}

const TelegramIntegration: React.FC<Props> = ({ isConnected, externalId, linkedAt, onRefresh, onDisconnect }) => {
  const [loading, setLoading] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [waiting, setWaiting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollCountRef = useRef(0);

  const stopPolling = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    pollCountRef.current = 0;
  };

  useEffect(() => {
    if (isConnected && waiting) {
      setWaiting(false);
      stopPolling();
    }
  }, [isConnected, waiting]);

  useEffect(() => () => stopPolling(), []);

  const handleConnect = async () => {
    setLoading(true);
    setError(null);
    try {
      const { url } = await getTelegramLink();
      window.open(url, '_blank', 'noopener,noreferrer');
      setWaiting(true);
      pollCountRef.current = 0;
      pollRef.current = setInterval(() => {
        pollCountRef.current += 1;
        onRefresh();
        if (pollCountRef.current >= 12) {
          stopPolling();
          setWaiting(false);
        }
      }, 5000);
    } catch {
      setError('Failed to get Telegram link. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDone = () => {
    stopPolling();
    setWaiting(false);
    onRefresh();
  };

  const handleCancel = () => {
    stopPolling();
    setWaiting(false);
  };

  const handleDisconnect = async () => {
    setDisconnecting(true);
    setError(null);
    try {
      await onDisconnect();
    } catch {
      setError('Failed to disconnect Telegram. Please try again.');
    } finally {
      setDisconnecting(false);
    }
  };

  if (isConnected) {
    return (
      <div className="space-y-2">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <LuCircleCheck className="text-success text-xl shrink-0" />
            <div>
              <div className="text-sm font-medium">Connected</div>
              {externalId && <div className="text-xs opacity-60">Chat ID: {externalId}</div>}
              {linkedAt && (
                <div className="text-xs opacity-60">Since {new Date(linkedAt).toLocaleDateString()}</div>
              )}
            </div>
          </div>
          <button className="btn btn-sm btn-outline btn-error" onClick={handleDisconnect} disabled={disconnecting}>
            {disconnecting ? <LuLoader className="animate-spin" /> : 'Disconnect'}
          </button>
        </div>
        {error && <div className="text-error text-sm">{error}</div>}
      </div>
    );
  }

  if (waiting) {
    return (
      <div className="space-y-3">
        <div className="flex items-center gap-2 text-sm opacity-70">
          <LuLoader className="animate-spin shrink-0" />
          Waiting for confirmation… Open the Telegram link on your phone and start the bot.
        </div>
        <div className="flex gap-2">
          <button className="btn btn-sm btn-primary" onClick={handleDone}>
            Done
          </button>
          <button className="btn btn-sm btn-ghost" onClick={handleCancel}>
            Cancel
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {error && <div className="text-error text-sm">{error}</div>}
      <div className="text-sm opacity-70">
        Get a one-time link to pair your Telegram account with MFlow. The link expires in 10 minutes.
      </div>
      <button className="btn btn-sm btn-primary gap-2" onClick={handleConnect} disabled={loading}>
        <FaTelegram />
        {loading ? 'Getting link…' : 'Connect Telegram'}
        {!loading && <LuExternalLink className="text-xs" />}
      </button>
    </div>
  );
};

export default TelegramIntegration;
